import Component from "../../lib/Component.js";

/**
 * MegaMenu Component (Minimal JS version)
 * CSS handles all show/hide logic via :hover and :checked
 * This only handles:
 * - Select navigation (dropdowns that navigate on change)
 * - Keyboard accessibility (Escape key to close)
 * - Body scroll lock when hamburger menu is open
 *
 * @typedef {{
 *  onMount: () => void|undefined,
 *  onUnmount: () => void|undefined
 * }} MegaMenuOptions
 */

/**
 * @param {HTMLElement} headerElement
 * @param {MegaMenuOptions} options
 * @returns {Component}
 */
export default function MegaMenu(headerElement, options = {}) {
  const elements = {
    header: headerElement,
    navigateSelects: headerElement.querySelectorAll("select[data-navigate]"),
    mobileMenuCheckboxes: headerElement.querySelectorAll(".mobile-menu-checkbox"),
    hamburgerCheckbox: headerElement.querySelector("#header-menu-checkbox"),
  };

  // Store event handler references for cleanup
  let handleResizeRef = null;
  let handleEscapeKeyRef = null;

  /**
   * Update ARIA expanded states for all mega menu triggers
   */
  const updateAriaStates = () => {
    elements.mobileMenuCheckboxes.forEach((checkbox) => {
      const trigger = checkbox.nextElementSibling;
      if (trigger && trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", checkbox.checked.toString());
      }
    });
  };

  /**
   * Set active menu section on mobile based on current path
   */
  const setActiveMenuOnMobile = () => {
    if (window.innerWidth >= 768) return;

    const path = location.pathname;

    // Determine which menu section should be active based on path
    const checkboxId = path.startsWith("/streaming/")
      ? "#mobile-menu-streaming-checkbox"
      : "#mobile-menu-premier-checkbox";

    const activeCheckbox = document.querySelector(checkboxId);
    if (activeCheckbox) {
      activeCheckbox.checked = true;
      updateAriaStates();
    }
  };

  /**
   * Handle body scroll lock when hamburger menu is open
   */
  const handleHamburgerMenuChange = () => {
    if (elements.hamburgerCheckbox && elements.hamburgerCheckbox.checked) {
      // Lock body scroll
      document.body.style.overflow = "hidden";
    } else {
      // Restore body scroll
      document.body.style.overflow = "";
    }
  };

  /**
   * Handle exclusive mobile section behavior (only one section open at a time)
   * @param {Event} event
   */
  const handleMobileSectionChange = (event) => {
    const currentCheckbox = event.target;

    // If this checkbox was just checked, uncheck all others
    if (currentCheckbox.checked) {
      elements.mobileMenuCheckboxes.forEach((checkbox) => {
        if (checkbox !== currentCheckbox && checkbox.checked) {
          checkbox.checked = false;
        }
      });
    }

    // Update ARIA states for all triggers
    updateAriaStates();
  };

  /**
   * Handle Escape key to close menus (accessibility requirement)
   * @param {KeyboardEvent} e
   */
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      // Close all mega menu sections
      elements.mobileMenuCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
      updateAriaStates();

      // Close hamburger menu if open
      if (elements.hamburgerCheckbox?.checked) {
        elements.hamburgerCheckbox.checked = false;
        document.body.style.overflow = "";
      }
    }
  };

  /**
   * Handle window resize to reset mobile menu state
   */
  const handleResize = () => {
    if (window.innerWidth >= 768) {
      // Close all mega menus when switching to desktop
      elements.mobileMenuCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
      updateAriaStates();
    } else {
      // Re-apply active section when switching to mobile
      setActiveMenuOnMobile();
    }
  };

  /**
   * Setup event listeners
   */
  const setupEventListeners = () => {
    // Navigation selects (dropdowns that navigate to URLs)
    elements.navigateSelects.forEach((select) => {
      select.addEventListener("change", (e) => {
        const url = e.target.value;
        if (url) {
          window.location.href = url;
        }
      });
    });

    // Handle hamburger menu body scroll lock
    if (elements.hamburgerCheckbox) {
      elements.hamburgerCheckbox.addEventListener("change", handleHamburgerMenuChange);
    }

    // Handle exclusive mobile menu sections
    elements.mobileMenuCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", handleMobileSectionChange);
    });

    // Handle Escape key for accessibility
    handleEscapeKeyRef = handleEscapeKey;
    document.addEventListener("keydown", handleEscapeKeyRef);

    // Handle window resize
    handleResizeRef = handleResize;
    window.addEventListener("resize", handleResizeRef);

    // Set initial active menu section on mobile
    setActiveMenuOnMobile();

    // Set initial ARIA states
    updateAriaStates();
  };

  /**
   * Clean up event listeners
   */
  const cleanupEventListeners = () => {
    if (elements.hamburgerCheckbox) {
      elements.hamburgerCheckbox.removeEventListener("change", handleHamburgerMenuChange);
    }

    elements.mobileMenuCheckboxes.forEach((checkbox) => {
      checkbox.removeEventListener("change", handleMobileSectionChange);
    });

    // Remove keyboard event listener
    if (handleEscapeKeyRef) {
      document.removeEventListener("keydown", handleEscapeKeyRef);
    }

    // Remove resize event listener
    if (handleResizeRef) {
      window.removeEventListener("resize", handleResizeRef);
    }

    // Restore body scroll on cleanup
    document.body.style.overflow = "";
  };

  const component = new Component({
    componentName: "MegaMenu",

    onMount() {
      setupEventListeners();

      if (options.onMount) {
        options.onMount();
      }
    },

    onUnmount() {
      cleanupEventListeners();

      if (options.onUnmount) {
        options.onUnmount();
      }
    },
  });

  component.el = headerElement;

  return component;
}
